import { Fragment, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronRight, MailX } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { BounceContactPanel } from '../components/BounceContactPanel';
import { apiFetch } from '../lib/api';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { SEARCH_DEBOUNCE_MS } from './KontakterListPage';
import { formatDateTime, formatNumber } from '../lib/format';
import {
  BOUNCE_REASON_INFO,
  BOUNCE_REASON_ORDER,
  SUB_TYPE_LABELS,
  type BounceReasonKind,
} from '../lib/bounceReasons';
import { cn } from '../lib/utils';
import type {
  BounceDetail,
  BounceReason,
  BounceSummaryResponse,
  BouncesListResponse,
} from '../types/api';

const PAGE_SIZE = 20;
const ALL_MAILS = 'all';

const DOT_CLASS: Record<BounceReasonKind, string> = {
  fix: 'bg-accent',
  done: 'bg-success-bar',
  hard: 'bg-danger-text',
  soft: 'bg-warning-marker',
  spam: 'bg-text',
};

/** The typos, open and dealt with, sit above a line in the side list. */
function isTypoGroup(r: BounceReason | undefined): boolean {
  return r === 'typo' || r === 'corrected';
}

function Dot({ kind, className }: { kind: BounceReasonKind; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('size-2 shrink-0 rounded-full', DOT_CLASS[kind], className)}
    />
  );
}

/** "Type / sub type" as SES reported it, in words. A complaint's type is its feedback type. */
export function describeBounceType(
  b: Pick<BounceDetail, 'kind' | 'bounceType' | 'subType'>,
): string {
  if (b.kind === 'complaint') return b.bounceType ? `Complaint (${b.bounceType})` : 'Complaint';
  if (!b.bounceType) return 'Not kept (recorded before 26 Oct 2026)';
  const sub = b.subType ? (SUB_TYPE_LABELS[b.subType] ?? b.subType) : null;
  return sub ? `${b.bounceType} / ${sub}` : b.bounceType;
}

function BounceRow({
  b,
  open,
  onToggle,
}: {
  b: BounceDetail;
  open: boolean;
  onToggle: () => void;
}) {
  const info = BOUNCE_REASON_INFO[b.reason];
  const [localPart] = b.email.split('@');
  const detailId = `bounce-detail-${b.id}`;
  return (
    <li className="border-b border-border-soft last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={detailId}
        onClick={onToggle}
        className="grid w-full grid-cols-[8px_minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 px-4 py-2.5 text-left hover:bg-hover md:grid-cols-[8px_minmax(0,1.5fr)_minmax(0,1.1fr)_minmax(0,1fr)_96px_16px] md:items-center"
      >
        <Dot kind={info.kind} className="row-start-1 mt-[7px] md:mt-0" />
        <span className="min-w-0 text-sm font-medium break-all">
          {b.email}
          {b.reason === 'typo' && b.suggestedDomain ? (
            <span className="block text-[13px] font-normal text-link">
              → {localPart}@{b.suggestedDomain}?
            </span>
          ) : b.reason === 'corrected' && b.suggestedDomain ? (
            <span className="block text-[13px] font-normal text-muted">
              → {localPart}@{b.suggestedDomain}
            </span>
          ) : null}
        </span>
        <span className="col-start-2 row-start-2 text-[13px] md:col-start-3 md:row-start-1 md:text-sm">
          {info.label}
          <span className="text-faint"> · {info.blocked ? 'blocked' : 'still mailed'}</span>
        </span>
        <span className="col-start-2 row-start-3 min-w-0 truncate text-xs text-muted md:col-start-4 md:row-start-1 md:text-[13px]">
          {b.campaign?.name ?? '—'}
        </span>
        <span className="col-start-3 row-start-1 text-xs text-faint tabular-nums md:col-start-5 md:text-right">
          {formatDateTime(b.occurredAt)}
        </span>
        <ChevronRight
          size={14}
          aria-hidden="true"
          className={cn(
            'hidden text-faint transition-transform md:block md:col-start-6',
            open && 'rotate-90',
          )}
        />
      </button>
      {open ? (
        <div
          id={detailId}
          className="flex flex-col gap-2 border-t border-border-soft bg-hover px-4 pt-3 pb-3.5 md:pl-10"
        >
          <p className="m-0 text-xs text-muted">
            <b className="font-semibold text-text">{info.blocked ? 'Blocked.' : 'Still mailed.'}</b>{' '}
            {info.effect.replace(/^(Blocked|Still mailed): /, '')}
          </p>
          {b.reason === 'typo' && b.suggestedDomain ? (
            <p className="m-0 text-xs text-muted">
              Looks like a typo: <b className="font-semibold text-text">{b.email.split('@')[1]}</b>{' '}
              → <b className="font-semibold text-text">{b.suggestedDomain}</b>?
            </p>
          ) : b.reason === 'corrected' && b.suggestedDomain ? (
            <p className="m-0 text-xs text-muted">
              Right address:{' '}
              <b className="font-semibold text-text">
                {localPart}@{b.suggestedDomain}
              </b>
            </p>
          ) : null}
          <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
            <dt className="text-faint">Bounce type</dt>
            <dd className="m-0 min-w-0 break-words">{describeBounceType(b)}</dd>
            <dt className="text-faint">Server answer</dt>
            <dd className="m-0 min-w-0 font-mono text-[12px] break-words text-muted">
              {b.diagnostic ??
                (b.kind === 'complaint'
                  ? 'The receiver pressed “Report spam”. No server answer.'
                  : 'No answer kept.')}
            </dd>
          </dl>
          <BounceContactPanel bounce={b} />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Audience → Bounces. Every bounce and spam complaint SES reports, sorted by
 * why the mail came back (typo, missing domain or mailbox, full mailbox,
 * refused, spam), each with what happens to the address. A row opens to the
 * receiving server's answer and the contact behind the address (signup,
 * orders; for a typo an admin can fix the address). Replaces the copies SES used to mail to the
 * sender. Kept 90 days, like the messages.
 */
export function BouncesPage() {
  const [reason, setReason] = useState<BounceReason | undefined>(undefined);
  const [campaign, setCampaign] = useState<string>(ALL_MAILS);
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q.trim(), SEARCH_DEBOUNCE_MS);
  const [openId, setOpenId] = useState<string | null>(null);
  // Keyset paging as on Contacts: Next pushes the server's `nextCursor`, Previous pops.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const campaignId = campaign === ALL_MAILS ? undefined : campaign;

  const summary = useQuery({
    queryKey: ['suppressions', 'bounces', 'summary', campaignId],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (campaignId) params.set('campaign', campaignId);
      return apiFetch<BounceSummaryResponse>(`/suppressions/bounces/summary?${params.toString()}`, {
        signal,
      });
    },
    // Keep the last counts while another mail's summary loads, so a picked reason isn't dropped in between.
    placeholderData: keepPreviousData,
  });

  // A reason with nothing in it (e.g. after picking another mail) falls back to All.
  const activeReason = reason && (summary.data?.reasons[reason] ?? 0) > 0 ? reason : undefined;

  const list = useQuery({
    queryKey: ['suppressions', 'bounces', activeReason, campaignId, debouncedQ, cursor],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (activeReason) params.set('reason', activeReason);
      if (campaignId) params.set('campaign', campaignId);
      if (debouncedQ) params.set('q', debouncedQ);
      if (cursor) params.set('cursor', cursor);
      params.set('limit', String(PAGE_SIZE));
      return apiFetch<BouncesListResponse>(`/suppressions/bounces?${params.toString()}`, {
        signal,
      });
    },
  });

  function reset() {
    setCursors([]);
    setOpenId(null);
  }

  const visibleReasons = BOUNCE_REASON_ORDER.filter((r) => (summary.data?.reasons[r] ?? 0) > 0);
  const info = activeReason ? BOUNCE_REASON_INFO[activeReason] : null;
  const failed = list.isError || summary.isError;

  function navButton(
    key: string,
    selected: boolean,
    label: string,
    count: number | undefined,
    kind: BounceReasonKind | null,
    onClick: () => void,
  ) {
    return (
      <li key={key} className="shrink-0 md:shrink">
        <button
          type="button"
          aria-current={selected}
          onClick={onClick}
          className={cn(
            'flex w-full items-center gap-2.5 whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-[13px] text-muted',
            'md:rounded-[9px] md:border-transparent md:bg-transparent md:px-2.5 md:py-2 md:whitespace-normal md:text-left',
            'hover:bg-card aria-[current=true]:border-border aria-[current=true]:bg-card aria-[current=true]:font-medium aria-[current=true]:text-text',
          )}
        >
          {kind ? <Dot kind={kind} /> : <span className="size-2 shrink-0" aria-hidden="true" />}
          <span className="md:flex-1">{label}</span>
          <span className="text-xs text-faint tabular-nums">
            {count === undefined ? '' : formatNumber(count)}
          </span>
        </button>
      </li>
    );
  }

  return (
    <>
      <PageHeader
        title="Bounces"
        badge={<MockBadge />}
        description="Mails that could not be delivered, and mails marked as spam, sorted by why. Kept for 90 days."
      />

      {failed ? <Alert>The bounces could not be loaded. Please try again.</Alert> : null}

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[240px_minmax(0,1fr)] md:gap-6">
        <nav aria-label="Reasons" className="min-w-0 md:sticky md:top-4">
          <ul className="m-0 flex list-none gap-1.5 overflow-x-auto p-0 pb-1 md:flex-col md:gap-0.5 md:overflow-visible md:pb-0">
            {navButton('all', !activeReason, 'All', summary.data?.total, null, () => {
              setReason(undefined);
              reset();
            })}
            {visibleReasons.map((r, i) => (
              <Fragment key={r}>
                {!isTypoGroup(r) && (i === 0 || isTypoGroup(visibleReasons[i - 1])) ? (
                  <li
                    key={`sep-${r}`}
                    aria-hidden="true"
                    className="mx-2.5 my-1.5 hidden h-px bg-border md:block"
                  />
                ) : null}
                {navButton(
                  r,
                  activeReason === r,
                  BOUNCE_REASON_INFO[r].label,
                  summary.data?.reasons[r],
                  BOUNCE_REASON_INFO[r].kind,
                  () => {
                    setReason(r);
                    reset();
                  },
                )}
              </Fragment>
            ))}
          </ul>
        </nav>

        <section
          aria-label={info ? info.label : 'All bounces'}
          className="min-w-0 overflow-hidden rounded-[14px] border border-border bg-card"
        >
          <header className="flex flex-col gap-1 border-b border-border px-4 py-4">
            <h2 className="m-0 flex items-baseline gap-2 text-base font-semibold">
              {info ? info.label : 'All bounces'}
              <span className="text-xs font-normal text-faint tabular-nums">
                {list.data ? formatNumber(list.data.total) : ''}
              </span>
            </h2>
            <p className="m-0 max-w-[64ch] text-[13px] text-muted">
              {info
                ? info.text
                : 'Every bounce and spam complaint from the last 90 days. Pick a reason to see what it means and what happens to the address.'}
            </p>
          </header>

          <div className="flex flex-wrap gap-2 border-b border-border-soft px-4 py-3">
            <div className="min-w-[200px] flex-1">
              <label htmlFor="bounce-search" className="sr-only">
                Search by email
              </label>
              <Input
                id="bounce-search"
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  reset();
                }}
                placeholder="Search email, e.g. gmail.com or anna@"
                autoComplete="off"
                className="h-9"
              />
            </div>
            {(summary.data?.campaigns.length ?? 0) > 0 ? (
              <Select
                value={campaign}
                onValueChange={(v) => {
                  setCampaign(v);
                  reset();
                }}
              >
                <SelectTrigger
                  aria-label="Mail"
                  className="h-9 w-full justify-between md:w-[240px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_MAILS}>All mails</SelectItem>
                  {summary.data?.campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>

          {list.isLoading ? (
            <div className="px-4 py-6 text-muted">Loading bounces…</div>
          ) : list.isError ? null : (list.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              className="border-0 py-8"
              icon={<MailX />}
              title={debouncedQ ? 'No address matches' : 'No bounces'}
              description={
                debouncedQ
                  ? 'No bounce or spam complaint in the last 90 days for this search.'
                  : 'Bounces and spam complaints from the last 90 days show up here.'
              }
            />
          ) : (
            <ul aria-label="Bounces" className="m-0 list-none p-0">
              {list.data?.items.map((b) => (
                <BounceRow
                  key={b.id}
                  b={b}
                  open={openId === b.id}
                  onToggle={() => setOpenId(openId === b.id ? null : b.id)}
                />
              ))}
            </ul>
          )}

          <footer className="flex items-center gap-2 border-t border-border px-4 py-2.5">
            <span className="text-xs text-faint">
              {list.data ? `${formatNumber(list.data.total)} in total · kept 90 days` : ''}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={cursors.length === 0}
                onClick={() => {
                  setCursors((s) => s.slice(0, -1));
                  setOpenId(null);
                }}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!list.data?.nextCursor}
                onClick={() => {
                  const next = list.data?.nextCursor;
                  if (next) {
                    setCursors((s) => [...s, next]);
                    setOpenId(null);
                  }
                }}
              >
                Next
              </Button>
            </div>
          </footer>
        </section>
      </div>
    </>
  );
}
